import React, { useState, useEffect, Suspense } from "react";
import { Card, Container, Col, Row, Form, Table, OverlayTrigger, Tooltip} from "react-bootstrap";
import { Helmet } from 'react-helmet-async';
import authenticatedFetch from "../../utils/fetch"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserMd, faBookMedical, faDatabase, faClipboardList, faBuilding } from "@fortawesome/free-solid-svg-icons";

import RevenueTeamLabel from "../../components/RevenueTeamLabel";
import LazyBar from "../../utils/LazyBar";
import LazyPie from "../../utils/LazyPie";
import LazyLine from "../../utils/LazyLine";

import rawTaskStatuses from "../../utils/taskStatuses";


const CHART_COLORS = ["#E8EAED", "#3AAFA9", "#2B7A78", "#DEF2F1", "#17252A", "#BFC9D1"];

const truncate = (str, max = 40) => {
  if (!str) return "";
  return str.length > max ? str.slice(0, max) + "..." : str;
};

const CarrierSelect = ({ practiceId, selectedCarrierId, handleCarrierChanged}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    setIsLoaded(false);
    setError(null);
    setData([]);
    if (practiceId > 0)
    {
        authenticatedFetch({
          path: "carrier/practice/" + practiceId,
          successHandler: function(result) {
            setIsLoaded(true);
            setData(result);
          },
          errorHandler: function(error) {
            setIsLoaded(true);
            setError(error);
          }
        });
    }
    else
    {
      authenticatedFetch({
        path: "carrier/revenueteam",
        successHandler: function(result) {
          setIsLoaded(true);
          setData(result);
        },
        errorHandler: function(error) {
          setIsLoaded(true);
          setError(error);
        }
      });
    }
  }, [practiceId])

  if (!isLoaded)
  {
    return (<Form.Select size="sm" disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>);
  }
  else if (error || !data)
  {
    return (<Form.Select size="sm" disabled={true}><option value={0} key={0}>Please reload...</option></Form.Select>);
  }
  else
  {
    let carrierOptionList = [<option value={0} key={0}>Filter by carrier...</option>];
    carrierOptionList.push(data.map((carrier, i) => {
      return (
        <option value={carrier.id} key={carrier.id}>{truncate(carrier.name)}</option>
      )
    }, this));

    carrierOptionList.push(<option value={-1} key={-1}>(No Carrier)</option>);
    return (
      <Form.Select size="sm" onChange={(e) => {handleCarrierChanged(e.target.value, e)}} value={selectedCarrierId}>{carrierOptionList}</Form.Select>
    )
  }
}

const PracticeSelect = ({ selectedPracticeId, handlePracticeChanged}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    authenticatedFetch({
      path: "practice/revenueteam",
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  }, [])

  if (!isLoaded)
  {
    return (<Form.Select size="sm" style={{width: "300px"}} disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>);
  }
  else if (error || !data)
  {
    return (<Form.Select size="sm" style={{width: "300px"}} disabled={true}><option value={0} key={0}>Please reload...</option></Form.Select>);
  }
  else
  {
    let practiceOptionList = [<option value={0} key={0}>All practices</option>];
    practiceOptionList.push(data.map((practice, i) => {
      return (
        <option value={practice.id} key={practice.id}>{truncate(practice.name)}</option>
      )
    }, this));

    return (
      <Form.Select size="sm" style={{width: "300px"}} onChange={(e) => {handlePracticeChanged(e.target.value, e)}} value={selectedPracticeId}>{practiceOptionList}</Form.Select>
    )
  }
}


const Statistics = ({practiceId}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    setIsLoaded(false);
    setError(null);
    setData(null);
    authenticatedFetch({
      path: "task/kpis/v2/statistics?practiceId=" + practiceId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  }, [practiceId])

  var content;
  if (error)
  {
     content = <Table striped bordered size="sm"><tbody><tr><td className='text-danger' style={{height: "80px"}}>Please reload...</td></tr></tbody></Table>
  }
  else if (!isLoaded || !data)
  {
    content = <Table striped bordered size="sm"><tbody><tr><td style={{height: "80px"}}>Loading...</td></tr></tbody></Table>
  }
  else
  {
    content = (
      <Table striped bordered size="sm">
        <tbody>
        <tr>
          <td style={{textAlign: "center", width: "100px"}}>
            <OverlayTrigger
              triggers={["hover","focus"]}
              placement="top"
              overlay={<Tooltip>Providers</Tooltip>}
            >
              <span><FontAwesomeIcon icon={faUserMd} size="2xl" style={{color: "#3AAFA9", width: "25px", height: "25px"}} /></span>
            </OverlayTrigger>
          </td>
          <td style={{textAlign: "center", width: "100px"}}>
            <OverlayTrigger
              triggers={["hover","focus"]}
              placement="top"
              overlay={<Tooltip>Carriers</Tooltip>}
            >
              <span><FontAwesomeIcon icon={faBookMedical} size="2xl" style={{color: "#3AAFA9", width: "25px", height: "25px"}} /></span>
            </OverlayTrigger>
          </td>
          <td style={{textAlign: "center", width: "100px"}}>
            <OverlayTrigger
              triggers={["hover","focus"]}
              placement="top"
              overlay={<Tooltip>Locations</Tooltip>}
            >
              <span><FontAwesomeIcon icon={faBuilding} size="2xl" style={{color: "#3AAFA9", width: "25px", height: "25px"}} /></span>
            </OverlayTrigger>
          </td>
          <td style={{textAlign: "center", width: "100px"}}>
            <OverlayTrigger
              triggers={["hover","focus"]}
              placement="top"
              overlay={<Tooltip>Hubs</Tooltip>}
            >
              <span><FontAwesomeIcon icon={faDatabase} size="2xl" style={{color: "#3AAFA9", width: "25px", height: "25px"}} /></span>
            </OverlayTrigger>
          </td>
          <td style={{textAlign: "center", width: "100px"}}>
            <OverlayTrigger
              triggers={["hover","focus"]}
              placement="top"
              overlay={<Tooltip>Active Tasks</Tooltip>}
            >
              <span><FontAwesomeIcon icon={faClipboardList} size="2xl" style={{color: "#3AAFA9", width: "25px", height: "25px"}} /></span>
            </OverlayTrigger>
          </td>
        </tr>
        <tr>
          <td style={{textAlign: "center", fontSize: "1.5em"}}>{data.totalProviders}</td>
          <td style={{textAlign: "center", fontSize: "1.5em"}}>{data.totalCarriers}</td>
          <td style={{textAlign: "center", fontSize: "1.5em"}}>{data.totalFacilities}</td>
          <td style={{textAlign: "center", fontSize: "1.5em"}}>{data.totalHubs}</td>
          <td style={{textAlign: "center", fontSize: "1.5em"}}>{data.totalActiveTasks}</td>
        </tr>
        </tbody>
      </Table>
    );
  }

  return (
    <Card>
      <Card.Body>
        {content}
      </Card.Body>
    </Card>
  );
};

const TaskCompletionCountByCategory = ({practiceId}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    setIsLoaded(false);
    setError(null);
    setData(null);
    authenticatedFetch({
      path: "task/kpis/v2/taskcompletioncountbycategory?practiceId=" + practiceId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  }, [practiceId])

  var content;
  if (error)
  {
    content = <><div style={{fontSize: "1.5em", textAlign: "center", width: "100%", height: "300px", paddingTop: "120px" }} className='text-danger'>Please reload...</div></>
  }
  else if (!isLoaded || !data)
  {
    content = <><div style={{fontSize: "1.5em", textAlign: "center", width: "100%", height: "300px", paddingTop: "120px"}}>Loading...</div></>
  }
  else
  {
    const values = data.timeSeriesList.map(ts => ts.data[0]);
    const maxVal = Math.max(...values);
    const buffer = Math.max(1, Math.ceil(maxVal * 0.05));

    const chartData = {
      labels: data.xAxisLabels,
      datasets: [
        {
        label: "Volume",
        data: values,
        backgroundColor: CHART_COLORS,  // colors per bar
        borderColor: CHART_COLORS,
        borderWidth: 1,
        barPercentage: 0.75,
        categoryPercentage: 0.5,
        
      }
    ]
    };

    const options = {
      maintainAspectRatio: false,
      legend: {
        display: false,
      },
      scales: {
        yAxes: [{
          stacked: false,
          ticks: {
            beginAtZero: true,
            max: maxVal + buffer,
            stepSize: 1,
          }
        }],
        xAxes: [
          {
            stacked: false,
            gridLines: {
              color: "transparent",
            },
          },
        ],
      },
    };

    content = (
      <div className="chart">
        <Suspense fallback={<div>Loading...</div>}>
          <LazyBar data={chartData} options={options}/>
        </Suspense>
      </div>
    );
  }

  return (
    <Card>
      <Card.Header style={{marginBottom: "-15px"}}>
        <Card.Title>Task Volume</Card.Title>
        <h6 className="card-subtitle text-muted">
          Tasks completed, by category, past 90 days
        </h6>
      </Card.Header>
      <Card.Body>
        {content}
      </Card.Body>
    </Card>
  );
};

const TaskCompletionByCategoryByMonth = ({practiceId}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState(null);
  const [selectedCarrierId, setSelectedCarrierId] = useState(0);

  useEffect(() => {
    setIsLoaded(false);
    setError(null);
    setData(null);
    authenticatedFetch({
      path: "task/kpis/v2/completioncountbymonthandtype?carrierId=" + selectedCarrierId + "&practiceId=" + practiceId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  }, [selectedCarrierId, practiceId])

  const handleCarrierChanged = (carrierId, e) => {
    e.preventDefault();
    setSelectedCarrierId(carrierId);
  }

  var content;
  if (error)
  {
    content = <><div style={{fontSize: "1.5em", textAlign: "center", width: "100%", height: "300px", paddingTop: "120px" }} className='text-danger'>Please reload...</div></>
  }
  else if (!isLoaded || !data)
  {
    content = <><div style={{fontSize: "1.5em", textAlign: "center", width: "100%", height: "300px", paddingTop: "120px"}}>Loading...</div></>
  }
  else
  {
    var datasets = [];
    for (var i=0; i<data["timeSeriesList"].length; i++)
    {
      datasets.push({
        label: data["timeSeriesList"][i]["label"],
          data: data["timeSeriesList"][i]["data"],
          backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
          borderColor: CHART_COLORS[i % CHART_COLORS.length],
          // fill: false,
          borderWidth: 2,
          fill: true,               // <-- makes it an area
          lineTension: 0.3,
          pointRadius: 0,           // cleaner area look
          pointHoverRadius: 4
      })
    }

    const chartData = {
      labels: data["xAxisLabels"],
      datasets: datasets
    };

    const options = {
      maintainAspectRatio: false,
      legend: {
        display: true,
        position: "bottom",
      },
      tooltips: {
        mode: "index",
        intersect: false
      },
      hover: {
        mode: "index",
        intersect: false
      },
      scales: {
        xAxes: [{
          stacked: true,          // <-- stack horizontally
          // gridLines: { color: "transparent" }
        }],
        yAxes: [{
          stacked: true,          // <-- stack vertically (sum of parts)
          ticks: {
            beginAtZero: true,
            callback: function(value) {
              return Number.isInteger(value) ? value : null; // no decimals
            }
          }
        }]
      }
    };

    content = (
      <div className="chart">
        <Suspense fallback={<div>Loading...</div>}>
          <LazyLine data={chartData} options={options} />
        </Suspense>
      </div>
    );
  }

  return (
    <Card>
      <Card.Header style={{marginBottom: "-15px"}}>
        <Card.Title>Total Monthly Task Volume<div className="float-end"><CarrierSelect practiceId={practiceId} selectedCarrierId={selectedCarrierId} handleCarrierChanged={handleCarrierChanged} /></div></Card.Title>
        <h6 className="card-subtitle text-muted">
          Tasks completed, by category, by month
        </h6>
      </Card.Header>
      <Card.Body>
        {content}
      </Card.Body>
    </Card>
  );
};

const InitialEnrollmentLagByCarrier = ({practiceId}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    setIsLoaded(false);
    setError(null);
    setData(null);
    authenticatedFetch({
      path: "task/kpis/v2/initialenrollmenttimebycarrier?practiceId=" + practiceId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  }, [practiceId])

  var content;
  if (error)
  {
    content = <><div style={{fontSize: "1.5em", textAlign: "center", width: "100%", height: "300px", paddingTop: "120px" }} className='text-danger'>Please reload...</div></>
  }
  else if (!isLoaded || !data)
  {
    content = <><div style={{fontSize: "1.5em", textAlign: "center", width: "100%", height: "300px", paddingTop: "120px"}}>Loading...</div></>
  }
  else
  {
    const cycledColors = data["timeSeriesList"][0]["data"].map((_, i) => 
      CHART_COLORS[i % CHART_COLORS.length]
    );

    const chartData = {
      labels: data["xAxisLabels"].map(label => truncate(label, 40)),
      datasets: [
        {
          label: "Days",
          data: data["timeSeriesList"][0]["data"],
          backgroundColor: cycledColors,
          borderColor: cycledColors,
          borderWidth: 1,
          barPercentage: 0.75,
          categoryPercentage: 0.5,
        }
      ]
    }

    const options = {
      maintainAspectRatio: false,
      legend: {
        display: false,
      },
      scales: {
        yAxes: [{
          stacked: false,
          ticks: {
            beginAtZero: true
          }
        }],
        xAxes: [
          {
            stacked: false,
            gridLines: {
              color: "transparent",
            },
          },
        ],
      },
    };

    content = (
      <div className="chart">
        <Suspense fallback={<div>Loading...</div>}>
          <LazyBar data={chartData} options={options} />
        </Suspense>
      </div>
    );
  }

  return (
    <Card>
      <Card.Header style={{marginBottom: "-15px"}}>
        <Card.Title>Initial Enrollment Lag</Card.Title>
        <h6 className="card-subtitle text-muted">
          Avg. # of <b>days</b> from submission to approval, in days, by carrier, past year
        </h6>
      </Card.Header>
      <Card.Body>
        {content}
      </Card.Body>
    </Card>
  );
};

const TaskDaysInStatus = ({practiceId}) => {

  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState(null);
  
  useEffect(() => {
    setIsLoaded(false);
    setError(null);
    setData(null);
    authenticatedFetch({
      path: "task/kpis/v2/timespentperstatus?practiceId=" + practiceId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  }, [practiceId])

  var content;
  if (error)
  {
    content = <><div style={{fontSize: "1.5em", textAlign: "center", width: "100%", height: "300px", paddingTop: "70px" }} className='text-danger'>Please reload...</div></>
  }
  else if (!isLoaded || !data)
  {
    content = <><div style={{fontSize: "1.5em", textAlign: "center", width: "100%", height: "300px", paddingTop: "70px"}}>Loading...</div></>
  }
  else
  {
    var colors = ["#3AAFA9", "#F8D7DA", "#F1AEB5", "#EA868F", "#BFC9D1"];

    const chartData = {
      labels: rawTaskStatuses.filter(ts => Object.keys(data["tasks"]).includes(ts.value)).map(ts => ts.label),
      datasets: [
        {
          data: Object.values(data["tasks"]),
          backgroundColor: colors,
          borderColor: "transparent",
        },
      ]
    };

    const options = {
      maintainAspectRatio: false,
      // cutoutPercentage: 45,
      legend: {
        display: true,
        position: "bottom",
      },
      title: {
        display: true,
        text: `Avg. ${Object.values(data["tasks"]).reduce((a, b) => a + b, 0)} days`,
        fontSize: 16,
      },
    };

    content = (
      <div className="chart chart-sm" style={{height: "300px"}}>
        <Suspense fallback={<div>Loading...</div>}>
          <LazyPie data={chartData} options={options} />
        </Suspense>
      </div>
    );
  }

  return (
    <Card>
      <Card.Header style={{marginBottom: "-15px"}}>
        <Card.Title>Task Hold Time</Card.Title>
        <h6 className="card-subtitle text-muted">
          Avg # of <b>days</b> spent <b>waiting</b>, by status, for tasks completed in past 90 days
        </h6>
      </Card.Header>
      <Card.Body>
          {content}
      </Card.Body>
    </Card>
  );
};


const KPIsV2 = () => {
  const [selectedPracticeId, setSelectedPracticeId] = useState(0);

  const handlePracticeChanged = (practiceId, e) => {
    e.preventDefault();
    setSelectedPracticeId(practiceId);
  };

  return (
    <React.Fragment>
      <Helmet title="KPIs" />
      <Container fluid className="p-0">
        <Row>
          <Col lg="12">
            <div className="d-flex align-items-center mb-3">
              <h1 className="h3 mb-0 me-3">
                KPIs&nbsp;&nbsp;|&nbsp;&nbsp;<RevenueTeamLabel />&nbsp;&nbsp;|
              </h1>
              <PracticeSelect
                handlePracticeChanged={handlePracticeChanged}
                selectedPracticeId={selectedPracticeId}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col lg="12">
            <Statistics practiceId={selectedPracticeId} />
          </Col>
        </Row>
        <Row>
          <Col lg="6">
            <TaskCompletionCountByCategory practiceId={selectedPracticeId} />
          </Col>
          <Col lg="6">
            <TaskCompletionByCategoryByMonth practiceId={selectedPracticeId} />
          </Col>
        </Row>
        <Row>
          <Col lg="6">
            <InitialEnrollmentLagByCarrier practiceId={selectedPracticeId} />
          </Col>
          <Col lg="6">
            <TaskDaysInStatus practiceId={selectedPracticeId} />
          </Col>
        </Row>
        
      </Container>
      </React.Fragment>
    )
  };
  
  export default KPIsV2;
  